import React from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../ui/Button';
import { calculateTotals, emptyLine, formatKr, lineTotal } from '../../utils/quote';
import { quoteUnits } from '../../types/quote';
import type { QuoteLine, QuoteUnit } from '../../types/quote';
interface Props {
  lines: QuoteLine[];
  onChange: (lines: QuoteLine[]) => void;
  disabled?: boolean;
}
const cell = 'w-full rounded-lg border border-line bg-white px-3 py-2 text-[15px] text-ink transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none disabled:cursor-not-allowed disabled:bg-canvas';
export function QuoteLinesEditor({
  lines,
  onChange,
  disabled = false
}: Props) {
  const totals = calculateTotals(lines);
  function update(id: string, patch: Partial<QuoteLine>) {
    onChange(lines.map(line => line.id === id ? {
      ...line,
      ...patch
    } : line));
  }
  return <div data-id="element-433" data-mp="1vg7996udill8">
      <div className="hidden grid-cols-[1fr_5rem_7rem_7rem_5rem_7rem_2.5rem] gap-2 px-1 pb-2 text-xs uppercase tracking-wider text-ink-muted md:grid" data-id="element-434" data-mp="1vgh355ijhcw7">
        <span data-id="element-435" data-mp="3ug0hzn2241yt">Beskrivelse</span>
        <span data-id="element-436" data-mp="3b91vutne0fl">Antal</span>
        <span data-id="element-437" data-mp="3ctvni851fce">Enhed</span>
        <span data-id="element-438" data-mp="3c1yi2yw79k0">Enhedspris</span>
        <span data-id="element-439" data-mp="8avfw6havkpg">Moms %</span>
        <span className="text-right" data-id="element-440" data-mp="8alm07t4wteh">I alt</span>
        <span data-id="element-441" data-mp="88sgivu9cf82" />
      </div>

      <ul className="space-y-3 md:space-y-2" data-id="element-442" data-mp="d1btbql4f6bt">
        {lines.map(line => <li key={line.id} className="grid gap-2 rounded-xl border border-line bg-canvas p-3 md:grid-cols-[1fr_5rem_7rem_7rem_5rem_7rem_2.5rem] md:items-center md:border-0 md:bg-transparent md:p-0" data-id="element-443" data-mp="d3etv4jwghus">
            <label className="md:contents" data-id="element-444" data-mp="r13d67ybjgxq">
              <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-445" data-mp="r260k5wfn18w">Beskrivelse</span>
              <input className={cell} value={line.description} disabled={disabled} placeholder="Fx montering af hegn, 12 meter" onChange={event => update(line.id, {
            description: event.target.value
          })} aria-label="Beskrivelse" data-id="element-446" data-mp="vzoe69lea4n8" />
            </label>

            <div className="grid grid-cols-2 gap-2 md:contents" data-id="element-447" data-mp="2lqbacbm8dt2m">
              <label className="md:contents" data-id="element-448" data-mp="2zpayli5u05y9">
                <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-449" data-mp="2zqcdjxpyxcl9">Antal</span>
                <input className={cell} type="number" min={0} step="0.25" inputMode="decimal" value={line.quantity} disabled={disabled} onChange={event => update(line.id, {
              quantity: Number(event.target.value)
            })} aria-label="Antal" data-id="element-450" data-mp="2upy9eqrhmmsi" />
              </label>

              <label className="md:contents" data-id="element-451" data-mp="3dhhc0whlnyu0">
                <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-452" data-mp="3sdglaagg450q">Enhed</span>
                <select className={`${cell} appearance-none`} value={line.unit} disabled={disabled} onChange={event => update(line.id, {
              unit: event.target.value as QuoteUnit
            })} aria-label="Enhed" data-id="element-453" data-mp="3nf5b1ym8nshz">
                  {quoteUnits.map(unit => <option key={unit} value={unit} data-id="element-454" data-mp="wv90jfjh8h3">
                      {unit}
                    </option>)}
                </select>
              </label>

              <label className="md:contents" data-id="element-455" data-mp="3nbweyzg8edp5">
                <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-456" data-mp="3s3efjmecec9l">Enhedspris</span>
                <input className={cell} type="number" min={0} step="1" inputMode="decimal" value={line.unitPrice} disabled={disabled} onChange={event => update(line.id, {
              unitPrice: Number(event.target.value)
            })} aria-label="Enhedspris" data-id="element-457" data-mp="3s594dbsqioiy" />
              </label>

              <label className="md:contents" data-id="element-458" data-mp="gipvmqsawh8u">
                <span className="mb-1 block text-xs text-ink-muted md:hidden" data-id="element-459" data-mp="gkiyrw3pgjc3">Moms %</span>
                <input className={cell} type="number" min={0} max={100} step="1" inputMode="numeric" value={line.vatRate} disabled={disabled} onChange={event => update(line.id, {
              vatRate: Number(event.target.value)
            })} aria-label="Momssats i procent" data-id="element-460" data-mp="bk50h5rwonr9" />
              </label>
            </div>

            <p className="flex items-center justify-between gap-2 text-[15px] font-medium text-ink md:justify-end" data-id="element-461" data-mp="3jzopfsljnk0i">
              <span className="text-xs font-normal text-ink-muted md:hidden" data-id="element-462" data-mp="3f26k0c6qjmx9">I alt inkl. moms</span>
              {formatKr(lineTotal(line))}
            </p>

            <button type="button" disabled={disabled} onClick={() => onChange(lines.filter(entry => entry.id !== line.id))} aria-label="Fjern linje" className="inline-flex h-9 w-9 items-center justify-center justify-self-end rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger disabled:opacity-50" data-id="element-463" data-mp="1d9qozhf9rgmc">
              <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-464" data-mp="11tlzceb6vee1" />
            </button>
          </li>)}
      </ul>

      {lines.length === 0 && <p className="rounded-xl border border-dashed border-line bg-canvas px-4 py-6 text-center text-sm text-ink-muted" data-id="element-465" data-mp="16syv5ok1fvsr">
          Ingen linjer endnu. Tilføj den første linje for at beregne prisen.
        </p>}

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-466" data-mp="1gojt9blzkbx8">
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => onChange([...lines, emptyLine()])} data-id="element-467" data-mp="2n66vzeq2v5kz">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-468" data-mp="s290if4du1ql" />
          Tilføj linje
        </Button>

        <dl className="min-w-[15rem] divide-y divide-line/70 text-[15px]" data-id="element-469" data-mp="n3xnjvisjb45">
          <div className="flex justify-between gap-6 py-2" data-id="element-470" data-mp="114i3mjxbmsty">
            <dt className="text-ink-soft" data-id="element-471" data-mp="x2n6zsqc53mi">Subtotal ekskl. moms</dt>
            <dd className="font-medium text-ink" data-id="element-472" data-mp="x352uo935ftw">{formatKr(totals.subtotal)}</dd>
          </div>
          <div className="flex justify-between gap-6 py-2" data-id="element-473" data-mp="9dkdnnmc9mc6">
            <dt className="text-ink-soft" data-id="element-474" data-mp="4ehjiy7wd0iz">Moms</dt>
            <dd className="font-medium text-ink" data-id="element-475" data-mp="4fslcshs7y7g">{formatKr(totals.vatAmount)}</dd>
          </div>
          <div className="flex justify-between gap-6 py-2" data-id="element-476" data-mp="i6nqbtp5tv5r">
            <dt className="font-medium text-ink" data-id="element-477" data-mp="dee68uaq7pbe">Total inkl. moms</dt>
            <dd className="font-display text-lg font-bold text-ink" data-id="element-478" data-mp="3lu7kf2hl4opa">{formatKr(totals.total)}</dd>
          </div>
        </dl>
      </div>
    </div>;
}